/**
 * Validation rules ported from the current Edit screen so the redesign does
 * not accept anything the existing one rejects.
 */

export const MAX_ADDITIONAL_INFO_LENGTH = 255;
export const MIN_TEMPLATE_NAME_LENGTH = 8;

/** The characters a template name may contain. */
const TEMPLATE_NAME_ALLOWED_CHARS = /^[a-zA-Z0-9-_&*$#!:|,./\\ ]+$/;

/**
 * The first character must be a letter, which rules out a leading space,
 * underscore or digit — and, with the trimmed length check below, a name made
 * entirely of spaces.
 */
const TEMPLATE_NAME_STARTS_WITH_LETTER = /^[a-zA-Z]/;

/**
 * Returns the first problem with a template name, or null when it is valid.
 * The start character is checked against the raw value so a leading space is
 * rejected rather than silently trimmed away; length is checked against the
 * trimmed value so padding does not count towards the minimum.
 */
export const validateTemplateName = (
  rawName: string,
  otherTemplateNames: string[],
): string | null => {
  if (!rawName || !rawName.trim()) {
    return "Template name is required";
  }

  if (!TEMPLATE_NAME_STARTS_WITH_LETTER.test(rawName)) {
    return "Template name must start with a letter — it cannot begin with a space, underscore or number";
  }

  if (!TEMPLATE_NAME_ALLOWED_CHARS.test(rawName)) {
    return "Template name contains characters that are not allowed";
  }

  const name = rawName.trim();
  if (name.length < MIN_TEMPLATE_NAME_LENGTH) {
    return `Template name must be at least ${MIN_TEMPLATE_NAME_LENGTH} characters long`;
  }

  const lowered = name.toLowerCase();
  const isDuplicate = otherTemplateNames.some(
    (other) => other.trim().toLowerCase() === lowered,
  );
  if (isDuplicate) {
    return "Template name must be unique";
  }

  return null;
};

export interface ValidationInput {
  templateName: string;
  componentSharedUid: string;
  html: string;
  css: string;
  additionalInfo: string;
  /** Names of the other templates on this site, for the uniqueness check. */
  otherTemplateNames: string[];
}

export const isValidHTML = (htmlCode: string): boolean => {
  if (!htmlCode) return true;

  const parsed = new DOMParser().parseFromString(htmlCode, "text/html");
  const hasParserError = parsed.querySelector("parsererror") !== null;
  const hasElement = Array.from(parsed.body.childNodes).some(
    (node) => node.nodeType === Node.ELEMENT_NODE,
  );

  return !hasParserError && hasElement;
};

/**
 * Parsed inside a detached document rather than by appending a <style> to
 * document.head — the current screen does the latter and leaks the user's CSS
 * into the admin UI.
 *
 * Deliberately lenient: CSS parsing drops bad rules instead of failing, so the
 * only thing treated as invalid is a stylesheet with real content where not a
 * single rule survived. Blocking a save on a false negative is worse than
 * letting unusual-but-parseable CSS through.
 */
export const isValidCSS = (cssCode: string): boolean => {
  if (!cssCode || !cssCode.trim()) return true;

  // Comment-only stylesheets legitimately produce zero rules.
  const withoutComments = cssCode.replace(/\/\*[\s\S]*?\*\//g, "").trim();
  if (!withoutComments) return true;

  try {
    const doc = document.implementation.createHTMLDocument("css-validation");
    const style = doc.createElement("style");
    style.textContent = cssCode;
    doc.head.appendChild(style);

    const sheet = style.sheet as CSSStyleSheet | null;
    if (!sheet) return true; // could not parse here; do not block the save

    return sheet.cssRules.length > 0;
  } catch {
    return true;
  }
};

export const isValidAdditionalInfo = (
  value: string,
): { valid: boolean; message?: string } => {
  if (!value || !value.trim()) return { valid: true };

  try {
    const parsed = JSON.parse(value);
    if (typeof parsed !== "object" || parsed === null) {
      return {
        valid: false,
        message: "Additional settings must be a valid JSON object",
      };
    }
  } catch {
    return {
      valid: false,
      message: "Additional settings must be a valid JSON object",
    };
  }

  if (value.length > MAX_ADDITIONAL_INFO_LENGTH) {
    return {
      valid: false,
      message: `Additional settings must not exceed ${MAX_ADDITIONAL_INFO_LENGTH} characters`,
    };
  }

  return { valid: true };
};

/** Returns the first problem found, or null when the form can be saved. */
export const validateTemplate = (input: ValidationInput): string | null => {
  if (!input.componentSharedUid || !input.html) {
    return "Please fill all the fields";
  }

  const nameProblem = validateTemplateName(
    input.templateName,
    input.otherTemplateNames,
  );
  if (nameProblem) return nameProblem;

  if (!isValidHTML(input.html)) {
    return "Invalid HTML Code";
  }

  if (!isValidCSS(input.css)) {
    return "Invalid CSS Code";
  }

  const additionalInfo = isValidAdditionalInfo(input.additionalInfo);
  if (!additionalInfo.valid) {
    return additionalInfo.message ?? "Invalid additional settings";
  }

  return null;
};
